import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider, type FieldErrors, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { WizardContainer, WizardNavigation } from '@/components/wizards';
import { Button, Card, ErrorSummary, Modal } from '@/components/common';
import { renewalRefusalOf } from '@/services/policies';
import { applyServerErrors } from '@/utils/formErrors';
import { useAuthStore } from '@/store/auth';
import {
  useInsuranceGroupsLookup,
  useInsuranceSubgroupsLookup,
  useInsuranceTypesLookup,
} from '@/hooks/useProducts';
import { useInsuranceTypeSchema, type FieldSchema } from '@/hooks/useInsuranceTypeSchema';
import { useCustomer, useRenewalPrefill, useSubmitQuoteRequest } from '@/hooks';
// By path, not `@/services`: that index is outside this task's file domain.
import { leadService, type LeadPolicyPrefill } from '@/services/leads';
import { RENEWAL_CUSTOMER_MISMATCH, type QuoteSubmitPayload, type QuoteSubmitResponse } from '@/types';
import {
  motorQuoteSchema,
  MOTOR_STEPS,
  motorStepsForBusinessType,
  motorStepFieldsFor,
  motorStepIdForField,
  normalizeStoredPincode,
  MOTOR_DEFAULT_VALUES,
  VEHICLE_DETAILS_STEP_ID,
  PRODUCT_PREMIUM_STEP_ID,
  VEHICLE_CATEGORY_OPTIONS,
  type MotorQuoteFormData,
  lifeQuoteSchema,
  LIFE_STEPS,
  LIFE_STEP_FIELDS,
  LIFE_DEFAULT_VALUES,
  type LifeQuoteFormData,
  healthQuoteSchema,
  HEALTH_STEPS,
  HEALTH_STEP_FIELDS,
  HEALTH_DEFAULT_VALUES,
  type HealthQuoteFormData,
  PLACEHOLDER_STEPS,
  PREMIUM_OPTIONS_STEP_ID,
  calculateAge,
} from './schema';
import {
  buildGenericQuotePayload,
  buildGenericQuoteSchema,
  buildGenericSteps,
  dynamicFields,
  genericDefaultValues,
  genericStepFields,
  sectionFromStepId,
  GENERIC_STEP_CUSTOMER,
  GENERIC_STEP_DETAILS,
  GENERIC_STEP_INSURANCE_TYPE,
  GENERIC_STEP_REVIEW,
  type GenericQuoteFormData,
} from './genericSchema';
import { useQuoteEnquiryDetail } from '../quoteEnquiries';
import { createGenericQuoteRequest } from './genericSubmit';
import {
  emptyPremiumOption,
  isPremiumOptionBlank,
  isPremiumOptionValid,
  postPremiumOptions,
  type PremiumOptionDraft,
} from './premiumOptions';
import { PremiumOptionsStep } from './steps/common';
import { usePremiumOptionsVehicleCategoryReset } from './steps/common/PremiumOptionsStep';
import {
  DynamicQuoteSection,
  GenericDetailsStep,
  GenericReviewStep,
} from './steps/generic';
import {
  InsuranceTypeStep,
  CustomerStep,
  BusinessTypeStep,
  ProductVariantStep,
  // The nine vehicle steps are composed by VehicleDetailsStep now, not
  // dispatched individually from here.
  VehicleDetailsStep,
  ReviewStep as MotorReviewStep,
} from './steps/motor';
import {
  BasicDetailsStep as LifeBasicDetailsStep,
  ProductDetailsStep as LifeProductDetailsStep,
  ReviewStep as LifeReviewStep,
  AwaitingIntegrationPanel,
} from './steps/life';
import {
  ProductDetailsStep as HealthProductDetailsStep,
  InsuredDetailsStep as HealthInsuredDetailsStep,
  PremiumDetailsStep as HealthPremiumDetailsStep,
  AdditionalDetailsStep as HealthAdditionalDetailsStep,
  ReviewStep as HealthReviewStep,
  AwaitingIntegrationPanel as HealthAwaitingIntegrationPanel,
} from './steps/health';

const FALLBACK_INSURANCE_TYPE_OPTIONS = [
  { value: 'motor', label: 'Motor Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'health', label: 'Health Insurance' },
];

/**
 * Parse a numeric form string, preserving a genuine 0. `Number(x) || undefined`
 * would turn a real 0 into undefined; this only drops empty/NaN.
 */
function numOrUndefined(value: string | undefined): number | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The three insurance types with a hand-written branch. EVERY other type is
 * driven by the schema-driven generic flow (`genericSchema.ts`).
 *
 * Motor is live in production and sync-priced via GoDigit, and Life/Health
 * carry bespoke multi-member / life-assured shapes the generic string form
 * cannot express. Replacing any of them with the generic path would risk a
 * working flow for no gain, so this set is the explicit opt-OUT and the
 * generic flow is the default for everything else.
 */
const HANDWRITTEN_BRANCHES = new Set(['motor', 'life', 'health']);

/**
 * Unified Quote Wizard container. Reads `?type=` (defaulting to motor) and
 * `?customer=` (to prefill the proposer from an existing customer, mirroring
 * the `/quotes/new?customer=<id>` links used across Customer/Policy/Lead/
 * Renewal detail pages), then renders the matching branch's step list.
 *
 * Motor (Task 2.5), Life (Task 2.6), and Health (Task 2.7) are fully built
 * out. Each gets its own `useForm` instance (different schemas/field sets)
 * rather than one polymorphic form — keeps every step component's
 * `useFormContext<XxxFormData>()` concretely typed instead of fighting a
 * union type through `FormProvider`.
 *
 * Every OTHER insurance type now gets a fourth, generic `useForm` whose schema,
 * steps and payload are derived at runtime from the type's
 * `InsuranceTypeFieldSchema` rows (`applies_to='quote'`) — the same registry
 * and the same `/products/types/{id}/schema/` endpoint the Policy create form
 * reads. There is no per-type code to write: configuring the type configures
 * the quote flow.
 *
 * All four branches share the Premium Options step, which lets the broker list
 * several insurance companies (company / product / amount / expiry / file) for
 * one quote. Those rows are POSTed to `/quotes/requests/{id}/options/` after
 * the request is created, so they land as ordinary `Quote` rows alongside
 * anything an insurer API priced.
 */
export function QuoteWizardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();

  const initialType = (searchParams.get('type') || 'motor').toLowerCase();
  const [insuranceType, setInsuranceType] = useState<string>(initialType);
  const [currentStep, setCurrentStep] = useState(0);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [canGoNext, setCanGoNext] = useState(true);
  // Life and Health have no insurer adapter (Task 2.4), so a successful
  // submit returns status="no_quotes_available" instead of anything to
  // compare — captured here so the wizard can swap to the informational
  // panel in place.
  const [lifeSubmitResult, setLifeSubmitResult] = useState<QuoteSubmitResponse | null>(null);
  const [healthSubmitResult, setHealthSubmitResult] = useState<QuoteSubmitResponse | null>(null);
  // B15 -- the fetched lead prefill, held until the insurance-type lookup
  // (fetchedInsuranceTypes, below) is ready to resolve its `cover` block
  // against, and a ref so it is applied to the form exactly once even
  // though that lookup can re-render this effect several times.
  const [leadPrefill, setLeadPrefill] = useState<LeadPolicyPrefill | null>(null);
  const leadPrefillAppliedRef = useRef(false);
  // B15 -- set once the lead-prefill effect (below) has resolved `?lead=` and
  // wants to land on Business Type; the ACTUAL jump waits for a second effect
  // that confirms `customer_id` is really in the form first (see there for
  // why the two are split).
  const [wantsBusinessTypeStep, setWantsBusinessTypeStep] = useState(false);

  // Multi-insurer premium options — shared by all four branches. Held locally
  // because a row carries a File and there is no quote request to attach it to
  // until submit succeeds; flushed by `finishWithPremiumOptions`.
  const [premiumOptions, setPremiumOptions] = useState<PremiumOptionDraft[]>(() => [
    emptyPremiumOption(),
  ]);
  const [premiumOptionsTouched, setPremiumOptionsTouched] = useState(false);
  const [flushingOptions, setFlushingOptions] = useState(false);
  const [genericSubmitting, setGenericSubmitting] = useState(false);

  const { data: fetchedInsuranceTypes, isLoading: typesLoading } = useInsuranceTypesLookup();
  const insuranceTypeOptions = useMemo(() => {
    const opts = (fetchedInsuranceTypes ?? []).map((t) => ({ value: t.code, label: t.name }));
    return opts.length ? opts : FALLBACK_INSURANCE_TYPE_OPTIONS;
  }, [fetchedInsuranceTypes]);

  const selectedType = useMemo(
    () => (fetchedInsuranceTypes ?? []).find((t) => t.code === insuranceType),
    [fetchedInsuranceTypes, insuranceType]
  );
  const insuranceTypeId = selectedType?.id;
  const insuranceTypeLabel =
    selectedType?.name ||
    insuranceTypeOptions.find((o) => o.value === insuranceType)?.label ||
    insuranceType;

  const customerId = searchParams.get('customer') || '';
  const { data: customer } = useCustomer(customerId);

  // B15 -- quoting a contact from its linked lead (`?lead=<id>&step=...`).
  // Read here, alongside every other deep-link param; applied further down
  // once the lead's own insurance type (below) can be resolved.
  const leadParam = searchParams.get('lead') || '';
  const stepParam = searchParams.get('step') || '';

  // Renewing an existing policy (`?renewal=<policy id | renewal id>`).
  //
  // The Policies list and the Renewal detail page have both linked here for a
  // long time, and this parameter was never read -- so "Create renewal quote"
  // opened a blank wizard and the broker re-keyed a vehicle we had already
  // sold a policy on. The two callers pass different id types; the server
  // resolves either.
  const renewalSource = searchParams.get('renewal') || '';
  const { data: renewalPrefill } = useRenewalPrefill(renewalSource);

  // B9 hop 1: the renewed policy's id rides on EVERY branch as the hidden
  // `previous_policy_id` field. A source the server says cannot be renewed
  // (already renewed, instalment term, never issued...) is NOT linked -- the
  // refusal is shown instead -- though business type stays "renewal", because
  // that is still what the broker is quoting.
  const renewalEligibility = renewalPrefill?.eligibility;
  const renewalRefusedMessage =
    renewalEligibility && renewalEligibility.eligible === false
      ? renewalEligibility.message || 'That policy cannot be linked as a renewal.'
      : '';
  const renewalPolicyId = renewalPrefill && !renewalRefusedMessage ? renewalPrefill.policy_id || '' : '';

  // B9 hop 3: the server refuses a link whose customer differs with
  // `renewal_customer_mismatch`; the broker confirms and the submit is resent
  // with `acknowledge_renewal_customer_mismatch`. The acknowledgement is keyed
  // to the exact policy + customer pairing confirmed, so picking another
  // customer afterwards asks again rather than riding on a stale "yes".
  const ackMismatchKeyRef = useRef('');
  const pendingMismatchKeyRef = useRef('');
  const [mismatchPrompt, setMismatchPrompt] = useState<string | null>(null);

  // Working an enquiry up into a quote (`?enquiry=<source>:<id>`).
  //
  // The Quote Requests tab is a READ-ONLY feed over two tables, so a web
  // enquiry could be read and nothing else -- to quote it, a broker started
  // Create Quote from scratch and re-keyed the name, email and phone that were
  // on the screen in front of them.
  //
  // A microsite enquiry is a `leads.Lead` and usually has no Customer row at
  // all, which is why this cannot go through `?customer=`: there is nothing to
  // point at. The contact details are copied from the enquiry itself.
  const enquiryParam = searchParams.get('enquiry') || '';
  const [enquirySource, enquiryId] = enquiryParam.includes(':')
    ? enquiryParam.split(/:(.+)/, 2)
    : ['', ''];
  const { data: enquiry } = useQuoteEnquiryDetail(enquirySource, enquiryId || undefined);

  // Hoisted above `steps` (moved up from further below) so its watched
  // business_type can drive motorStepsForBusinessType — the Prior Policy
  // step (Wave F2 item 3a) only exists in the step list for Renewal/Rollover.
  const motorMethods = useForm<MotorQuoteFormData>({
    resolver: zodResolver(motorQuoteSchema),
    mode: 'onChange',
    defaultValues: MOTOR_DEFAULT_VALUES,
  });
  const lifeMethods = useForm<LifeQuoteFormData>({
    resolver: zodResolver(lifeQuoteSchema),
    mode: 'onChange',
    defaultValues: LIFE_DEFAULT_VALUES,
  });
  const healthMethods = useForm<HealthQuoteFormData>({
    resolver: zodResolver(healthQuoteSchema),
    mode: 'onChange',
    defaultValues: HEALTH_DEFAULT_VALUES,
  });

  const isMotor = insuranceType === 'motor';
  const isLife = insuranceType === 'life';
  const isHealth = insuranceType === 'health';
  // Generic only once the type has actually resolved to a configured
  // InsuranceType — a bogus `?type=` still falls back to the placeholder step
  // rather than rendering a flow that could never submit.
  const isGeneric = !HANDWRITTEN_BRANCHES.has(insuranceType) && !!insuranceTypeId;

  // ---------------------------------------------------------------------
  // Generic (schema-driven) branch
  // ---------------------------------------------------------------------
  // The zod schema is rebuilt every time the field schema loads or the
  // group/subgroup narrows it, but `useForm` captures its `resolver` prop once.
  // Indirect through a ref (seeded with the field-less schema so it is valid
  // from the very first render) and keep the ref pointed at the latest.
  const genericResolverRef = useRef<Resolver<GenericQuoteFormData>>(
    zodResolver(buildGenericQuoteSchema([])) as Resolver<GenericQuoteFormData>
  );
  const genericResolver = useCallback<Resolver<GenericQuoteFormData>>(
    (values, context, options) => genericResolverRef.current(values, context, options),
    []
  );

  const genericMethods = useForm<GenericQuoteFormData>({
    mode: 'onChange',
    defaultValues: genericDefaultValues([]) as GenericQuoteFormData,
    resolver: genericResolver,
  });
  const genericGroup = genericMethods.watch('insurance_group');
  const genericSubgroup = genericMethods.watch('insurance_subgroup');

  // The SAME registry + endpoint the Policy create form reads, asked for the
  // quote-side rows (`applies_to='quote'`, which the resolver unions with
  // 'both'). Disabled for motor/life/health so the live branches make no extra
  // request and cannot be affected by a schema change.
  const { data: resolvedSchema, isLoading: schemaLoading } = useInsuranceTypeSchema(
    isGeneric ? insuranceTypeId : undefined,
    genericGroup || undefined,
    genericSubgroup || undefined,
    'quote'
  );
  // Choosing a Group/Sub-group re-resolves the schema under a narrower scope,
  // and react-query drops `data` to undefined while the new key loads. Without
  // this the whole step list would collapse to the four fixed steps and the
  // form the user was mid-way through would blink out. Hold the last resolved
  // fields FOR THIS INSURANCE TYPE and keep rendering them until the narrower
  // set arrives; a change of type resets to empty so one type's fields can
  // never leak into another's.
  const lastSchemaRef = useRef<{ typeId?: string; fields: FieldSchema[] }>({ fields: [] });
  if (resolvedSchema?.fields && insuranceTypeId) {
    lastSchemaRef.current = { typeId: insuranceTypeId, fields: resolvedSchema.fields };
  }
  const schemaFields = useMemo(() => {
    if (resolvedSchema?.fields) return resolvedSchema.fields;
    return lastSchemaRef.current.typeId === insuranceTypeId ? lastSchemaRef.current.fields : [];
  }, [resolvedSchema, insuranceTypeId]);
  const genericFields = useMemo(() => dynamicFields(schemaFields), [schemaFields]);

  const genericZodResolver = useMemo(
    () => zodResolver(buildGenericQuoteSchema(schemaFields)) as Resolver<GenericQuoteFormData>,
    [schemaFields]
  );
  genericResolverRef.current = genericZodResolver;

  // Register newly-arrived schema keys as empty strings so they are controlled
  // from the first render (an undefined value makes React flip the input from
  // uncontrolled to controlled). Keyed on the field-key signature, so it runs
  // once per distinct schema and never clobbers something already typed.
  const genericFieldKeySignature = genericFields.map((f) => f.key).join('|');
  useEffect(() => {
    if (!isGeneric) return;
    const current = genericMethods.getValues() as Record<string, unknown>;
    for (const field of genericFields) {
      if (current[field.key] === undefined) {
        genericMethods.setValue(field.key, '', { shouldValidate: false, shouldDirty: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeneric, genericFieldKeySignature]);

  const { data: genericGroups } = useInsuranceGroupsLookup(isGeneric ? insuranceTypeId : undefined);
  const { data: genericSubgroups } = useInsuranceSubgroupsLookup(
    isGeneric ? genericGroup || undefined : undefined
  );

  const motorBusinessType = motorMethods.watch('business_type');

  const steps = useMemo(() => {
    if (isMotor) return motorStepsForBusinessType(motorBusinessType);
    if (isLife) return LIFE_STEPS;
    if (isHealth) return HEALTH_STEPS;
    if (isGeneric) return buildGenericSteps(schemaFields);
    // `?type=` matched no configured insurance type at all.
    return PLACEHOLDER_STEPS;
  }, [isMotor, isLife, isHealth, isGeneric, motorBusinessType, schemaFields]);
  const stepId = steps[currentStep]?.id;

  // Premium options are a step of their own on life/health/generic. On motor
  // they are a section of the combined "Product & Premium Selection" step,
  // which ALSO carries a real form field (product_variant) — so that step
  // gates on both, and cannot take the form-free shortcut the others do.
  const premiumStepId = isMotor ? PRODUCT_PREMIUM_STEP_ID : PREMIUM_OPTIONS_STEP_ID;
  const onPremiumStep = stepId === premiumStepId;

  // Vehicle Details carries the prior-policy section, which is only required
  // for Renewal/Rollover — so the gate depends on business_type, not just the
  // step id. `motorStepFieldsFor` keeps that in step with the section's own
  // render condition in VehicleDetailsStep.
  const motorStepFields = useMemo(
    () => (isMotor ? motorStepFieldsFor(stepId, motorBusinessType) : []),
    [isMotor, stepId, motorBusinessType]
  );
  const lifeStepFields = useMemo(
    () => (isLife ? LIFE_STEP_FIELDS[stepId ?? ''] ?? [] : []),
    [isLife, stepId]
  );
  const healthStepFields = useMemo(
    () => (isHealth ? HEALTH_STEP_FIELDS[stepId ?? ''] ?? [] : []),
    [isHealth, stepId]
  );
  // Flattened set of every mandatory field across the whole Health flow —
  // used ONLY on the terminal "review" step so "Calculate Premium" stays
  // disabled until the ENTIRE flow validates (not just the empty field list
  // for "review" itself), per the brief's explicit requirement. Motor/Life
  // are untouched: their own "review" step field lists stay empty, so their
  // Complete button keeps its existing (more permissive) behavior.
  const healthAllFields = useMemo(
    () => Array.from(new Set(Object.values(HEALTH_STEP_FIELDS).flat())),
    []
  );
  const genericStepFieldNames = useMemo(
    () => (isGeneric ? genericStepFields(stepId, schemaFields) : []),
    [isGeneric, stepId, schemaFields]
  );

  // A premium-option row that has been started but is incomplete/invalid blocks
  // Next (and Submit): a half-typed option would 400 on the options POST after
  // the quote request has already been created. A row left entirely blank is
  // ignored — the step is optional.
  const premiumOptionsBlocked = useMemo(
    () => premiumOptions.some((o) => !isPremiumOptionBlank(o) && !isPremiumOptionValid(o)),
    [premiumOptions]
  );
  const pendingPremiumOptions = useMemo(
    () => premiumOptions.filter((o) => !isPremiumOptionBlank(o)),
    [premiumOptions]
  );

  // Deep-link prefill (Wave F1 item 4b): /quotes/new?customer=<id> links from
  // Customer/Policy/Lead/Renewal detail pages still pre-select that customer
  // into the new Customer step, without forcing it — the CustomerStep can
  // still change the selection. Only fires once per linked customer (empty
  // customer_id check) so it never fights a deliberate change made in the
  // step itself.
  useEffect(() => {
    if (!isMotor || !customer) return;
    if (motorMethods.getValues('customer_id')) return;
    motorMethods.setValue('customer_id', customer.id, { shouldValidate: true });
    motorMethods.setValue('customer_name', customer.full_name || '');
    motorMethods.setValue('customer_phone', customer.phone || '');
    motorMethods.setValue('customer_email', customer.email || '');
    motorMethods.setValue('customer_dob', customer.date_of_birth || '');
    motorMethods.setValue('customer_gender', customer.gender || '');
    // B-02/C1: the proposer's PIN is a real form field now rather than being
    // read off this record at submit time, so the deep link must snapshot it
    // like every other identity field.
    motorMethods.setValue('customer_pincode', normalizeStoredPincode(customer.pincode), {
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMotor, customer]);

  // Same deep-link prefill for the generic branch. It reuses the motor
  // CustomerStep (identical field names), so it needs the identical snapshot.
  useEffect(() => {
    if (!isGeneric || !customer) return;
    if (genericMethods.getValues('customer_id')) return;
    genericMethods.setValue('customer_id', customer.id, { shouldValidate: true });
    genericMethods.setValue('customer_name', customer.full_name || '');
    genericMethods.setValue('customer_phone', customer.phone || '');
    genericMethods.setValue('customer_email', customer.email || '');
    genericMethods.setValue('customer_dob', customer.date_of_birth || '');
    genericMethods.setValue('customer_gender', customer.gender || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeneric, customer]);

  // Prefill from the enquiry being worked up. Only fills a field the broker has
  // not already filled, and never overrides the `?customer=` prefill above -- a
  // linked contact is better identification than three loose strings.
  //
  // Split per branch rather than picking `methods` from a ternary: the two
  // UseFormSetValue types are for different schemas, so their union has no
  // callable signature and every setValue below would need casting away.
  useEffect(() => {
    if (!enquiry || !isMotor) return;
    if (motorMethods.getValues('customer_id')) return;
    if (enquiry.name && !motorMethods.getValues('customer_name')) {
      motorMethods.setValue('customer_name', enquiry.name, { shouldValidate: true });
    }
    if (enquiry.phone && !motorMethods.getValues('customer_phone')) {
      motorMethods.setValue('customer_phone', enquiry.phone, { shouldValidate: true });
    }
    if (enquiry.email && !motorMethods.getValues('customer_email')) {
      motorMethods.setValue('customer_email', enquiry.email, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiry, isMotor]);

  useEffect(() => {
    if (!enquiry || !isGeneric) return;
    if (genericMethods.getValues('customer_id')) return;
    if (enquiry.name && !genericMethods.getValues('customer_name')) {
      genericMethods.setValue('customer_name', enquiry.name, { shouldValidate: true });
    }
    if (enquiry.phone && !genericMethods.getValues('customer_phone')) {
      genericMethods.setValue('customer_phone', enquiry.phone, { shouldValidate: true });
    }
    if (enquiry.email && !genericMethods.getValues('customer_email')) {
      genericMethods.setValue('customer_email', enquiry.email, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiry, isGeneric]);

  // Prefill the whole motor form from the policy being renewed.
  //
  // Only fills a field the broker has not already filled, so re-rendering (or
  // arriving with `?customer=` as well) never overwrites a deliberate edit.
  // `business_type` is forced to 'renewal' because that is what the caller
  // said this is, and it decides whether the Prior Policy step exists at all.
  useEffect(() => {
    if (!isMotor || !renewalPrefill) return;

    const setIfEmpty = (field: keyof MotorQuoteFormData, value: unknown) => {
      if (value === null || value === undefined || value === '') return;
      if (motorMethods.getValues(field)) return;
      motorMethods.setValue(field, value as never, { shouldValidate: true });
    };

    // The vehicle is the point of the exercise -- this is what the broker was
    // re-typing.
    const v = renewalPrefill.vehicle;
    if (v) {
      setIfEmpty('vehicle_category', v.vehicle_category);
      setIfEmpty('make_id', v.make_id);
      setIfEmpty('make_name', v.make_name);
      setIfEmpty('model_id', v.model_id);
      setIfEmpty('model_name', v.model_name);
      setIfEmpty('variant_id', v.variant_id);
      setIfEmpty('variant_name', v.variant_name);
      setIfEmpty('fuel_type', v.fuel_type);
      setIfEmpty('registration_number', v.registration_number);
      setIfEmpty('registration_date', v.registration_date);
      setIfEmpty('rto_code', v.rto_code);
      setIfEmpty('rto_label', v.rto_label);
      if (v.manufacturing_year) {
        setIfEmpty('manufacturing_year', String(v.manufacturing_year));
      }
    }

    const prior = renewalPrefill.prior_policy;
    if (prior) {
      setIfEmpty('previous_policy_number', prior.previous_policy_number);
      setIfEmpty('previous_insurer_id', prior.previous_insurer_id);
      setIfEmpty('previous_insurer_name', prior.previous_insurer_name);
      setIfEmpty('previous_policy_expiry', prior.previous_policy_expiry);
      if (prior.previous_ncb !== null && prior.previous_ncb !== undefined) {
        setIfEmpty('previous_ncb', String(prior.previous_ncb));
      }
      if (prior.has_claim_in_previous !== null && prior.has_claim_in_previous !== undefined) {
        setIfEmpty('has_claim_in_previous', prior.has_claim_in_previous ? 'yes' : 'no');
      }
    }

    // The customer comes through even when the policy had no vehicle row, so
    // the renewal quote still lands on the right contact.
    const c = renewalPrefill.customer;
    if (c && !motorMethods.getValues('customer_id')) {
      motorMethods.setValue('customer_id', c.id, { shouldValidate: true });
      motorMethods.setValue('customer_name', c.full_name || '');
      motorMethods.setValue('customer_phone', c.phone || '');
      motorMethods.setValue('customer_email', c.email || '');
      motorMethods.setValue('customer_dob', c.date_of_birth || '');
      motorMethods.setValue('customer_gender', c.gender || '');
      motorMethods.setValue('customer_pincode', normalizeStoredPincode(c.pincode), {
        shouldValidate: true,
      });
    }

    // Not `setIfEmpty`: the schema defaults business_type to 'new', so it is
    // never empty, and this link means renewal by definition.
    if (motorMethods.getValues('business_type') !== 'renewal') {
      motorMethods.setValue('business_type', 'renewal', { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMotor, renewalPrefill]);

  // B9 hop 1, every branch: the motor effect above used to be the only place
  // `?renewal=` was read, and it returns early for life/health/generic -- so a
  // renewal quote on any other line lost the link. Written into all four forms
  // because the broker may switch insurance type after arriving; never over an
  // id already set (e.g. by the motor vehicle-check prompt).
  useEffect(() => {
    if (!renewalPolicyId) return;
    if (!motorMethods.getValues('previous_policy_id')) {
      motorMethods.setValue('previous_policy_id', renewalPolicyId);
    }
    if (!lifeMethods.getValues('previous_policy_id')) {
      lifeMethods.setValue('previous_policy_id', renewalPolicyId);
    }
    if (!healthMethods.getValues('previous_policy_id')) {
      healthMethods.setValue('previous_policy_id', renewalPolicyId);
    }
    if (!genericMethods.getValues('previous_policy_id')) {
      genericMethods.setValue('previous_policy_id', renewalPolicyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewalPolicyId]);

  // B15 -- quoting a contact from its linked lead (`?lead=<id>`).
  //
  // Fetched separately from being APPLIED (below): the customer/broker match
  // check needs to run exactly once when the response lands, regardless of
  // how many times the insurance-type lookup it is later matched against
  // re-renders this component. A lead belonging to another contact or
  // broker (the latter is a plain 404 -- `policy_prefill` is scoped by
  // `get_queryset`) is never applied to the form; either way this is only
  // ever a courtesy on top of the `?customer=` prefill, never a requirement
  // for it.
  useEffect(() => {
    if (!leadParam) return;
    let cancelled = false;
    leadPrefillAppliedRef.current = false;
    setLeadPrefill(null);

    leadService
      .getPolicyPrefill(leadParam)
      .then((prefill) => {
        if (cancelled) return;
        if (customerId && prefill.customer !== customerId) {
          toast.error(
            "This lead isn't linked to this contact, so the quote wasn't prefilled from it."
          );
          return;
        }
        setLeadPrefill(prefill);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load the linked lead — starting from Customer Details.');
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadParam, customerId]);

  // Apply the fetched lead prefill exactly once, once the insurance-type
  // lookup it is matched against has also loaded (`leadPrefillAppliedRef`
  // survives the re-renders that loading causes). Business Type and Vehicle
  // Details only exist on the Motor branch, so they are set only when the
  // resolved -- or, failing that, the branch already active -- insurance
  // type is motor; a lead about something else legitimately carries an
  // empty vehicle block and no business_type signal anyway, so this mostly
  // falls through to a no-op rather than needing its own guard everywhere.
  useEffect(() => {
    if (!leadPrefill || leadPrefillAppliedRef.current || typesLoading) return;
    leadPrefillAppliedRef.current = true;

    // Insurance Type: switch branch only when the lead's product interest
    // resolves to a DIFFERENT configured type. An unmatched interest
    // ('other', or one with no InsuranceType behind it -- `cover.insurance_type`
    // is already '' for both, per the server) leaves the branch exactly as
    // it already was -- Motor, the default for a brand-new quote -- rather
    // than guessing one.
    const coverTypeId = leadPrefill.cover?.insurance_type || '';
    const matchedType = coverTypeId
      ? (fetchedInsuranceTypes ?? []).find((t) => t.id === coverTypeId)
      : undefined;
    if (matchedType && matchedType.code !== insuranceType) {
      setInsuranceType(matchedType.code);
      const next = new URLSearchParams(searchParams);
      next.set('type', matchedType.code);
      setSearchParams(next, { replace: true });
    }

    const effectiveCode = matchedType ? matchedType.code : insuranceType;
    if (effectiveCode !== 'motor') return;

    const setIfEmpty = (field: keyof MotorQuoteFormData, value: unknown) => {
      if (value === null || value === undefined || value === '') return;
      if (motorMethods.getValues(field)) return;
      motorMethods.setValue(field, value as never, { shouldValidate: true });
    };

    const v = leadPrefill.vehicle || {};
    if (v.vehicle_type && VEHICLE_CATEGORY_OPTIONS.some((o) => o.value === v.vehicle_type)) {
      setIfEmpty('vehicle_category', v.vehicle_type);
    }
    if (v.vehicle_make) {
      setIfEmpty('make_id', v.vehicle_make);
      setIfEmpty('make_name', v.vehicle_make_text);
    }
    if (v.vehicle_model) {
      setIfEmpty('model_id', v.vehicle_model);
      setIfEmpty('model_name', v.vehicle_model_text);
    }
    if (v.vehicle_variant) {
      setIfEmpty('variant_id', v.vehicle_variant);
      setIfEmpty('variant_name', v.vehicle_variant_text);
    }
    setIfEmpty('fuel_type', v.fuel_type_text);
    setIfEmpty('registration_number', v.vehicle_number);
    if (v.manufacturing_year) setIfEmpty('manufacturing_year', v.manufacturing_year);

    // The schema defaults business_type to 'new' so it is never naturally
    // empty -- overwrite it, INCLUDING a deliberate '' when the lead gave
    // neither signal, while it is still sitting at that untouched default.
    // An explicit '' matches none of BUSINESS_TYPE_OPTIONS, so the step
    // renders with nothing selected and the required-field gate on
    // 'business-type' refuses Next until the broker makes an explicit
    // choice -- the design's "otherwise blank" must never be silently
    // guessed away as "New" just because that happens to be the form's
    // resting default.
    if (motorMethods.getValues('business_type') === 'new') {
      motorMethods.setValue(
        'business_type',
        (leadPrefill.business_type || '') as MotorQuoteFormData['business_type'],
        { shouldValidate: true }
      );
    }

    // Request landing on Business Type -- but the ACTUAL jump is performed by
    // the effect below, only once `customer_id` has really been set into the
    // form by the `?customer=` prefill effect above. That effect depends on
    // `useCustomer(customerId)` resolving, which can lose the race against
    // this one (or fail outright): jumping unconditionally here would leave
    // the wizard sitting on Business Type with an empty `customer_id`, past
    // the one gate that used to stop that, and Submit would then silently
    // refuse at Review with nothing rendered to explain why. Requesting the
    // jump and waiting for confirmation keeps a slow/failed customer fetch on
    // step 0 -- exactly its pre-B15 behaviour -- instead.
    if (stepParam === 'business_type') {
      setWantsBusinessTypeStep(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadPrefill, typesLoading, fetchedInsuranceTypes]);

  // See the comment above: only jump once `customer_id` is genuinely present
  // in the form, not merely requested. Watches the field directly (rather
  // than the `customer` query) so it also covers the renewal/enquiry
  // customer-prefill paths, and re-checks itself out via
  // `wantsBusinessTypeStep` so it can never re-fire after the broker
  // deliberately clears the field back out on the Customer step.
  const motorCustomerIdForBusinessTypeJump = motorMethods.watch('customer_id');
  useEffect(() => {
    if (!wantsBusinessTypeStep || !motorCustomerIdForBusinessTypeJump) return;
    const idx = MOTOR_STEPS.findIndex((s) => s.id === 'business-type');
    if (idx >= 0) setCurrentStep(idx);
    setWantsBusinessTypeStep(false);
  }, [wantsBusinessTypeStep, motorCustomerIdForBusinessTypeJump]);

  const motorWatchedValues = motorMethods.watch();
  const lifeWatchedValues = lifeMethods.watch();
  const healthWatchedValues = healthMethods.watch();
  const genericWatchedValues = genericMethods.watch();

  // Next/Complete is disabled until the current step's mandatory fields pass —
  // re-checked reactively (not just on click) as the brief requires.
  useEffect(() => {
    let cancelled = false;
    // The standalone Premium Options step is not part of any react-hook-form
    // instance, so it gates on its own row validity alone. Motor's combined
    // step is excluded here: it has a form field of its own and falls through
    // to the per-branch dispatch, which ANDs the two gates together.
    if (onPremiumStep && !isMotor) {
      setCanGoNext(!premiumOptionsBlocked);
      return;
    }
    if (isGeneric) {
      if (genericStepFieldNames.length === 0) {
        setCanGoNext(true);
      } else {
        genericMethods.trigger(genericStepFieldNames).then((ok) => {
          if (!cancelled) setCanGoNext(ok && !premiumOptionsBlocked);
        });
      }
    } else if (isMotor) {
      if (motorStepFields.length === 0) {
        setCanGoNext(!(onPremiumStep && premiumOptionsBlocked));
      } else {
        motorMethods.trigger(motorStepFields).then((ok) => {
          if (!cancelled) setCanGoNext(ok && !(onPremiumStep && premiumOptionsBlocked));
        });
      }
    } else if (isLife) {
      if (lifeStepFields.length === 0) {
        setCanGoNext(true);
      } else {
        lifeMethods.trigger(lifeStepFields).then((ok) => {
          if (!cancelled) setCanGoNext(ok);
        });
      }
    } else if (isHealth) {
      const fieldsToCheck = stepId === 'review' ? healthAllFields : healthStepFields;
      if (fieldsToCheck.length === 0) {
        setCanGoNext(true);
      } else {
        healthMethods.trigger(fieldsToCheck).then((ok) => {
          if (!cancelled) setCanGoNext(ok);
        });
      }
    } else {
      setCanGoNext(true);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMotor,
    isLife,
    isHealth,
    isGeneric,
    currentStep,
    stepId,
    premiumOptionsBlocked,
    genericStepFieldNames,
    JSON.stringify(motorWatchedValues),
    JSON.stringify(lifeWatchedValues),
    JSON.stringify(healthWatchedValues),
    JSON.stringify(genericWatchedValues),
  ]);

  const submitMutation = useSubmitQuoteRequest();

  const brokerId = user?.tenant_type === 'broker' ? user.tenant_id ?? '' : '';

  const proposerName =
    customer?.full_name ||
    (user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.full_name : '') ||
    '';
  const proposerPhone = customer?.phone || user?.phone || '';
  const proposerEmail = customer?.email || user?.email || '';
  const proposerDob = customer?.date_of_birth || user?.date_of_birth || undefined;
  const proposerGender = customer?.gender || user?.gender || undefined;

  const handleInsuranceTypeChange = (code: string) => {
    setInsuranceType(code);
    // Every other branch opens on Insurance Type, so step 0 was the same thing
    // as "stay where you are". Motor now opens on Customer Details and carries
    // Insurance Type third, so landing on 0 would throw the user backwards out
    // of the step they are standing on. Land on the new branch's own Insurance
    // Type step instead.
    setCurrentStep(code === 'motor' ? MOTOR_STEPS.findIndex((s) => s.id === 'insurance-type') : 0);
    setLifeSubmitResult(null);
    setHealthSubmitResult(null);
    // Products are scoped to the insurance type, so an option picked for the
    // previous type names a product this type does not sell. Start clean.
    setPremiumOptions([emptyPremiumOption()]);
    setPremiumOptionsTouched(false);
    const next = new URLSearchParams(searchParams);
    next.set('type', code);
    setSearchParams(next, { replace: true });
  };

  /**
   * POST the drafted premium options onto a freshly created quote request, then
   * run `done`.
   *
   * When there are no options this is fully synchronous and calls `done`
   * immediately — that is deliberate, so a motor submit with no hand-entered
   * options navigates to the comparison screen exactly as fast as it did
   * before this step existed. `postPremiumOptions` never rejects (it collects
   * per-row failures), so `done` always runs and a failed attachment can never
   * strand the user on a request that was in fact created.
   */
  const finishWithPremiumOptions = (quoteRequestId: string, done: () => void) => {
    if (pendingPremiumOptions.length === 0) {
      done();
      return;
    }
    setFlushingOptions(true);
    postPremiumOptions(quoteRequestId, pendingPremiumOptions)
      .then((result) => {
        if (result.created) {
          toast.success(
            `${result.created} premium option${result.created === 1 ? '' : 's'} added`
          );
        }
        for (const failure of result.failures) {
          toast.error(`${failure.label}: ${failure.message}`);
        }
      })
      .catch(() => {
        toast.error('The quote was created but its premium options could not be saved.');
      })
      .finally(() => {
        setFlushingOptions(false);
        done();
      });
  };

  const handleNext = async () => {
    if (onPremiumStep) {
      setPremiumOptionsTouched(true);
      if (premiumOptionsBlocked) return;
    }
    let ok = true;
    if (isGeneric) {
      ok = genericStepFieldNames.length
        ? await genericMethods.trigger(genericStepFieldNames)
        : true;
    } else if (isMotor) {
      ok = motorStepFields.length ? await motorMethods.trigger(motorStepFields) : true;
    } else if (isLife) {
      ok = lifeStepFields.length ? await lifeMethods.trigger(lifeStepFields) : true;
    } else if (isHealth) {
      ok = healthStepFields.length ? await healthMethods.trigger(healthStepFields) : true;
    }
    if (ok && currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  /** B9 hop 3: the top-level renewal fields for an integrations submit. */
  const renewalLinkFields = (
    previousPolicyId: string | undefined,
    customerKey: string
  ): Pick<QuoteSubmitPayload, 'previous_policy_id' | 'acknowledge_renewal_customer_mismatch'> => {
    if (!previousPolicyId) return {};
    const acknowledged = ackMismatchKeyRef.current === `${previousPolicyId}:${customerKey}`;
    return {
      previous_policy_id: previousPolicyId,
      ...(acknowledged ? { acknowledge_renewal_customer_mismatch: true } : {}),
    };
  };

  /**
   * B9 hop 3: a refused renewal link. Returns true when handled. A customer
   * mismatch not yet acknowledged for this pairing opens the confirm; any
   * other refusal is shown as its sentence -- never the bare `code`, which the
   * generic field-error mapping would otherwise list as a message.
   */
  const handleRenewalRefusal = (
    error: unknown,
    previousPolicyId: string | undefined,
    customerKey: string
  ): boolean => {
    const refusal = renewalRefusalOf(error);
    if (!refusal) return false;
    const key = `${previousPolicyId ?? ''}:${customerKey}`;
    if (refusal.code === RENEWAL_CUSTOMER_MISMATCH && ackMismatchKeyRef.current !== key) {
      pendingMismatchKeyRef.current = key;
      setMismatchPrompt(refusal.message);
      return true;
    }
    setServerErrors([refusal.message]);
    return true;
  };

  const onMotorSubmit = (data: MotorQuoteFormData) => {
    if (!brokerId) {
      setServerErrors(['Your account is not linked to a broker — cannot submit a quote request.']);
      return;
    }
    // Customer step (item 4b) gates Next on customer_id, so by the time this
    // runs the form's own snapshot is authoritative — the URL-driven
    // proposerName/proposerPhone (deep link / logged-in user fallback) are
    // only used as a defensive fallback if that snapshot is somehow missing.
    const motorProposerName = data.customer_name || proposerName;
    const motorProposerPhone = data.customer_phone || proposerPhone;
    const motorProposerEmail = data.customer_email || proposerEmail;
    const motorProposerDob = data.customer_dob || proposerDob;
    const motorProposerGender = data.customer_gender || proposerGender;

    if (!motorProposerName || !motorProposerPhone) {
      setServerErrors(['Select or add a customer on the Customer step before submitting.']);
      return;
    }

    const payload: QuoteSubmitPayload = {
      product_type: `motor_${data.product_variant}`,
      business_type: data.business_type,
      broker_id: brokerId,
      ...renewalLinkFields(data.previous_policy_id, data.customer_id || motorProposerPhone),
      proposer: {
        name: motorProposerName,
        phone: motorProposerPhone,
        email: motorProposerEmail || undefined,
        dob: motorProposerDob || undefined,
        gender: motorProposerGender || undefined,
        // F1 follow-up (round-2 Wave F3): the Customer step gates Next/Complete
        // on customer_id being set (required in motorQuoteSchema), so this is
        // always populated by the time onMotorSubmit runs -- links
        // QuoteRequest.customer on the backend.
        customer_id: data.customer_id || undefined,
        // Motor is zone-rated on the pincode, and insurers refuse a quote
        // without one.
        //
        // B-02/C1: this read ONLY `customer?.pincode`, and that `customer` is
        // the `?customer=<id>` DEEP-LINK lookup -- so a quote started normally
        // from /quotes/new sent no pincode at all, and a customer whose record
        // has a blank one (59 of 408 live) could not supply it by any route.
        // `customer_pincode` is the Customer step's own field now: prefilled
        // from the record, required, six digits. The deep-link record stays as
        // the fallback rather than being dropped.
        pincode: data.customer_pincode?.trim() || customer?.pincode || undefined,
      },
      vehicle: {
        // Send the chosen VehicleVariant id + type/cover so the user's selection
        // actually drives the GoDigit quote (not the PRIVATE_CAR/comprehensive default).
        vehicle_variant_id: data.variant_id,
        vehicle_type: data.vehicle_category,
        product_variant: data.product_variant,
        registration_number:
          data.business_type === 'new' && data.registration_pending
            ? ''
            : (data.registration_number || '').toUpperCase(),
        make: data.make_name || '',
        model: data.model_name || '',
        variant: data.variant_name || '',
        manufacturing_year: Number(data.manufacturing_year) || undefined,
        rto_code: data.rto_code,
        fuel_type: data.fuel_type || '',
        // Registration Date step (item 9b) — required and shown on Review, but
        // was captured and never sent, silently losing the value on submit.
        registration_date: data.registration_date || undefined,
        // B-04: from the registration lookup (RegistrationNumberStep). The
        // quote -> policy conversion writes these onto the policy's vehicle.
        engine_number: data.engine_number || undefined,
        chassis_number: data.chassis_number || undefined,
        // Prior Policy (item 3a) — only ever populated when business_type is
        // Renewal/Rollover (PriorPolicyStep is skipped for New), but sent
        // unconditionally here same as every other optional vehicle field;
        // blank/undefined is a harmless no-op on the backend for New business.
        previous_policy_number: data.previous_policy_number || undefined,
        previous_insurer_id: data.previous_insurer_id || undefined,
        previous_insurer: data.previous_insurer_name || undefined,
        previous_policy_expiry: data.previous_policy_expiry || undefined,
        ncb_percentage: numOrUndefined(data.previous_ncb),
        has_claim_in_previous: data.has_claim_in_previous === 'yes',
      },
    };

    setServerErrors([]);
    submitMutation.mutate(payload, {
      onSuccess: (response) => {
        toast.success('Quote request submitted');
        // BROKER-2: pricing runs synchronously inside this POST, so the
        // response already carries the final outcome (quotes_created,
        // no_quotes_available + reason). Hand it to the comparison page so
        // it can render immediately instead of blind-polling for 6s and
        // then showing a bare "No quotes available".
        const requestId = response.quote_request_id || response.request_id;
        finishWithPremiumOptions(requestId, () =>
          navigate(`/quotes/compare/${requestId}`, { state: { submitResult: response } })
        );
      },
      onError: (error) => {
        if (handleRenewalRefusal(error, data.previous_policy_id, data.customer_id || motorProposerPhone)) return;
        const messages = applyServerErrors(error, motorMethods.setError);
        setServerErrors(messages);
      },
    });
  };

  /**
   * A client-side refusal must never be a silent click.
   *
   * B-02/C1 review: `MOTOR_STEP_FIELDS.review` is empty, so Submit is always
   * enabled at Review, and nothing on this page renders react-hook-form's own
   * errors (`ErrorSummary` shows server messages only). A schema failure on a
   * field belonging to a step the broker was never SHOWN -- the lead deep link
   * `?customer=<id>&lead=<id>&step=business_type` jumps straight past Customer,
   * and `customer_pincode` is blank for the customers whose record has none --
   * therefore produced a click that did nothing at all: no message, no
   * navigation, no request. That is the same failure the deep-link comment
   * above warns about, so name the refusals and go to the step that owns the
   * first one.
   */
  const onMotorInvalid = (errors: FieldErrors<MotorQuoteFormData>) => {
    const names = Object.keys(errors) as (keyof MotorQuoteFormData)[];
    const messages = names
      .map((name) => (errors[name] as { message?: string } | undefined)?.message)
      .filter((message): message is string => Boolean(message));
    setServerErrors(
      messages.length
        ? messages
        : ['Some required answers are still missing — check the earlier steps.']
    );
    const businessType = motorMethods.getValues('business_type');
    const owningStepId = names
      .map((name) => motorStepIdForField(name, businessType))
      .find((stepId): stepId is string => Boolean(stepId));
    const index = owningStepId ? steps.findIndex((s) => s.id === owningStepId) : -1;
    if (index >= 0) setCurrentStep(index);
  };

  const onLifeSubmit = (data: LifeQuoteFormData) => {
    if (!brokerId) {
      setServerErrors(['Your account is not linked to a broker — cannot submit a quote request.']);
      return;
    }

    const fullProposerName = [data.first_name, data.middle_name, data.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (!fullProposerName || !proposerPhone) {
      setServerErrors([
        'A proposer name and phone number are required. Link a customer via ?customer= or complete your profile.',
      ]);
      return;
    }

    // "Same as Proposer" is auto-copied onto life_assured_* live by
    // BasicDetailsStep, so this is only a distinct identity when the toggle
    // is "no" — otherwise there's nothing extra to send beyond the proposer.
    const lifeAssured =
      data.life_assured_same_as_proposer === 'no'
        ? {
            first_name: data.life_assured_first_name || '',
            middle_name: data.life_assured_middle_name || '',
            last_name: data.life_assured_last_name || '',
            gender: data.life_assured_gender || '',
            // Omit (not "") when blank so the backend DateField doesn't 400.
            date_of_birth: data.life_assured_date_of_birth || undefined,
            age: data.life_assured_age,
          }
        : null;

    const payload: QuoteSubmitPayload = {
      product_type: 'life',
      broker_id: brokerId,
      ...renewalLinkFields(data.previous_policy_id, proposerPhone),
      proposer: {
        name: fullProposerName,
        phone: proposerPhone,
        email: proposerEmail || undefined,
        // Life captures its own DOB/gender (rather than reusing the linked
        // customer/user silently, like motor does), so these are authoritative.
        dob: data.date_of_birth || undefined,
        gender: data.gender || undefined,
      },
      life: {
        // Persist onto QuoteRequest.additional_data.life_data. LifeSerializer
        // (backend/apps/integrations/serializers.py) now DECLARES every field
        // below, so all of them survive validation into life_data — see
        // task-2.6-report.md. `numOrUndefined` preserves a genuine 0.
        sum_assured: numOrUndefined(data.sum_assured),
        policy_term_years: numOrUndefined(data.policy_term_years),
        premium_payment_term: numOrUndefined(data.premium_payment_term_years),
        smoker: data.smoking_tobacco_usage === 'yes',
        annual_income: numOrUndefined(data.annual_income),
        premium_payment_frequency: data.premium_payment_frequency,
        existing_customer: data.existing_customer === 'yes',
        first_time_buyer: data.first_time_buyer === 'yes',
        premium_holiday: data.premium_holiday === 'yes',
        proposer_relationship: data.proposer_relationship,
        plan_variant: data.plan_variant,
        life_benefit_option: data.life_benefit_option,
        medical_non_medical: data.medical_non_medical,
        occupation: data.occupation,
        employment_type: data.employment_type,
        education_qualification: data.education_qualification,
        residence_status: data.residence_status,
        pincode: data.pincode,
        proposer_state: data.proposer_state_name || data.proposer_state_id,
        sales_person_state: data.sales_person_state_name || data.sales_person_state_id,
        life_assured_same_as_proposer: data.life_assured_same_as_proposer === 'yes',
        life_assured: lifeAssured,
      },
    };

    setServerErrors([]);
    submitMutation.mutate(payload, {
      onSuccess: (response) => {
        const requestId = response.quote_request_id || response.request_id;
        if (response.status === 'no_quotes_available') {
          // Life has no adapter, so any priced option on this request is one
          // the broker typed in — attach them before the terminal panel
          // replaces the wizard.
          finishWithPremiumOptions(requestId, () => setLifeSubmitResult(response));
          return;
        }
        // Defensive: if an adapter is ever wired up for life, fall back to
        // the same comparison flow motor uses.
        toast.success('Quote request submitted');
        // BROKER-2: pricing runs synchronously inside this POST, so the
        // response already carries the final outcome (quotes_created,
        // no_quotes_available + reason). Hand it to the comparison page so
        // it can render immediately instead of blind-polling for 6s and
        // then showing a bare "No quotes available".
        finishWithPremiumOptions(requestId, () =>
          navigate(`/quotes/compare/${requestId}`, { state: { submitResult: response } })
        );
      },
      onError: (error) => {
        if (handleRenewalRefusal(error, data.previous_policy_id, proposerPhone)) return;
        const messages = applyServerErrors(error, lifeMethods.setError);
        setServerErrors(messages);
      },
    });
  };

  const onHealthSubmit = (data: HealthQuoteFormData) => {
    if (!brokerId) {
      setServerErrors(['Your account is not linked to a broker — cannot submit a quote request.']);
      return;
    }
    // Health has no dedicated proposer-identity step (unlike Life); it
    // reuses the linked customer/user's identity silently, the same way
    // Motor does.
    if (!proposerName || !proposerPhone) {
      setServerErrors([
        'A proposer name and phone number are required. Link a customer via ?customer= or complete your profile.',
      ]);
      return;
    }

    const payload: QuoteSubmitPayload = {
      product_type: 'health',
      // Also sent top-level so QuoteRequest.business_type (whose
      // BUSINESS_TYPE_CHOICES include "new"/"portability") is set correctly —
      // duplicated inside health{} below per the brief's explicit
      // HealthSerializer extension list.
      business_type: data.business_type,
      broker_id: brokerId,
      ...renewalLinkFields(data.previous_policy_id, proposerPhone),
      proposer: {
        name: proposerName,
        phone: proposerPhone,
        email: proposerEmail || undefined,
        dob: proposerDob || undefined,
        gender: proposerGender || undefined,
      },
      health: {
        // Persist onto QuoteRequest.additional_data.health_data, with
        // `members` also landing on QuoteRequest.members_data. HealthSerializer
        // (backend/apps/integrations/serializers.py) now DECLARES every field
        // below, so all of them survive validation — see task-2.7-report.md.
        // `numOrUndefined` preserves a genuine 0 (e.g. children: 0).
        sum_insured: numOrUndefined(data.sum_insured),
        policy_term_years: numOrUndefined(data.policy_term_years),
        // Merge the two role-keyed arrays (adults first, then children) into
        // the single `members` list the backend expects. The forms keep each
        // member's data pinned to its role (see InsuredDetailsStep), so this
        // ordered concat is the only place the two ever combine.
        members: [...data.adult_members, ...data.child_members].map((m) => ({
          relation: m.relation,
          date_of_birth: m.date_of_birth,
          age: calculateAge(m.date_of_birth),
        })),
        pincode: data.pincode,
        business_type: data.business_type,
        state: data.state || '',
        city: data.city || '',
        adults: numOrUndefined(data.adults),
        children: numOrUndefined(data.children),
        applicant_annual_income: numOrUndefined(data.applicant_annual_income),
        pre_existing_condition: data.pre_existing_condition === 'yes',
        nri_discount: data.nri_discount === 'yes',
        bureau_score_discount: data.bureau_score_discount === 'yes',
      },
    };

    setServerErrors([]);
    submitMutation.mutate(payload, {
      onSuccess: (response) => {
        const requestId = response.quote_request_id || response.request_id;
        if (response.status === 'no_quotes_available') {
          finishWithPremiumOptions(requestId, () => setHealthSubmitResult(response));
          return;
        }
        // Defensive: if an adapter is ever wired up for health, fall back to
        // the same comparison flow motor uses.
        toast.success('Quote request submitted');
        // BROKER-2: pricing runs synchronously inside this POST, so the
        // response already carries the final outcome (quotes_created,
        // no_quotes_available + reason). Hand it to the comparison page so
        // it can render immediately instead of blind-polling for 6s and
        // then showing a bare "No quotes available".
        finishWithPremiumOptions(requestId, () =>
          navigate(`/quotes/compare/${requestId}`, { state: { submitResult: response } })
        );
      },
      onError: (error) => {
        if (handleRenewalRefusal(error, data.previous_policy_id, proposerPhone)) return;
        const messages = applyServerErrors(error, healthMethods.setError);
        setServerErrors(messages);
      },
    });
  };

  /**
   * The generic branch creates a `QuoteRequest` directly rather than going via
   * `/integrations/quotes/submit/`: that endpoint resolves `product_type` to
   * exactly one of motor/health/life and then demands the matching nested
   * block, so it can only ever serve the three hand-written branches. The
   * broker is stamped server-side from the caller in `perform_create`, so the
   * payload never names one.
   */
  const onGenericSubmit = async (data: GenericQuoteFormData) => {
    if (!brokerId) {
      setServerErrors(['Your account is not linked to a broker — cannot submit a quote request.']);
      return;
    }
    if (!insuranceTypeId) {
      setServerErrors(['Select an insurance type before submitting.']);
      return;
    }

    // The Customer step gates Next on customer_id, so the form's own snapshot
    // is authoritative; the deep-link / logged-in-user values are only a
    // defensive fallback.
    const name = data.customer_name || proposerName;
    const phone = data.customer_phone || proposerPhone;
    if (!name || !phone) {
      setServerErrors(['Select or add a customer on the Customer step before submitting.']);
      return;
    }

    const payload = buildGenericQuotePayload({
      insuranceTypeCode: insuranceType,
      data,
      fields: schemaFields,
      proposerName: name,
      proposerPhone: phone,
      proposerEmail: data.customer_email || proposerEmail,
      proposerDob: data.customer_dob || proposerDob,
      proposerGender: data.customer_gender || proposerGender,
    });
    // B9 hop 3 on the quotes route: `previous_policy` is already in the payload
    // (buildGenericQuotePayload); the acknowledgement is added only for the
    // pairing the broker confirmed.
    if (data.previous_policy_id && ackMismatchKeyRef.current === `${data.previous_policy_id}:${data.customer_id || phone}`) {
      payload.acknowledge_renewal_customer_mismatch = true;
    }

    setServerErrors([]);
    setGenericSubmitting(true);
    try {
      const created = await createGenericQuoteRequest(payload);
      toast.success('Quote request created');
      finishWithPremiumOptions(created.id, () => navigate(`/quotes/${created.id}`));
    } catch (error) {
      if (handleRenewalRefusal(error, data.previous_policy_id, data.customer_id || phone)) return;
      const messages = applyServerErrors(error, genericMethods.setError);
      setServerErrors(messages);
    } finally {
      setGenericSubmitting(false);
    }
  };

  const handleComplete = () => {
    // Defence in depth: the Premium Options step already refuses to advance
    // while a started row is incomplete, so this should be unreachable. If it
    // ever is reached, sending anyway would create the quote request and THEN
    // 400 on the option — send the user back to the row instead.
    if (premiumOptionsBlocked) {
      setPremiumOptionsTouched(true);
      setServerErrors(['Complete or remove the unfinished premium option before submitting.']);
      const index = steps.findIndex((s) => s.id === premiumStepId);
      if (index >= 0) setCurrentStep(index);
      return;
    }
    if (isMotor) {
      motorMethods.handleSubmit(onMotorSubmit, onMotorInvalid)();
      return;
    }
    if (isLife) {
      lifeMethods.handleSubmit(onLifeSubmit)();
      return;
    }
    if (isHealth) {
      healthMethods.handleSubmit(onHealthSubmit)();
      return;
    }
    if (isGeneric) {
      genericMethods.handleSubmit(onGenericSubmit)();
      return;
    }
    toast('Choose an insurance type to continue — this one is not configured yet.');
  };

  /** B9 hop 3: the broker confirmed a renewal for a different customer. */
  const confirmRenewalCustomerMismatch = () => {
    ackMismatchKeyRef.current = pendingMismatchKeyRef.current;
    setMismatchPrompt(null);
    handleComplete();
  };

  // B9: shown under the error summary on every branch.
  const renewalLinkUi = (
    <>
      {renewalRefusedMessage && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          This quote will not be linked as a renewal of that policy: {renewalRefusedMessage}
        </div>
      )}
      <Modal isOpen={!!mismatchPrompt} onClose={() => setMismatchPrompt(null)} title="Different customer">
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">{mismatchPrompt}</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">Quote it as the renewal anyway?</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setMismatchPrompt(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmRenewalCustomerMismatch}>
              Link as renewal anyway
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );

  // B14: the motor branch's own selected vehicle type, scoping the Premium
  // Options step's Product dropdown to that vehicle type's products. Every
  // other branch (life, health, generic) has no such field and passes
  // `undefined`, which leaves the step's filtering exactly as it was.
  const motorVehicleCategory = motorMethods.watch('vehicle_category');
  const premiumOptionsVehicleCategory = isMotor ? motorVehicleCategory : undefined;

  // B5: the motor branch's own Product Variant selection (Comprehensive /
  // Third Party Only / Standalone OD), scoping the Premium Options step's
  // Product dropdown to that variant's PolicyType -- same "undefined on every
  // other branch" rule as the vehicle category above.
  const motorProductVariant = motorMethods.watch('product_variant');
  const premiumOptionsProductVariant = isMotor ? motorProductVariant : undefined;

  // B14: the rows' products were chosen under a vehicle type. When that type
  // changes -- the broker went back to Vehicle Details and picked another --
  // every row's product is cleared HERE, at the owner of the options state.
  // The step itself is unmounted while the type is being changed, so it cannot
  // do this; see `usePremiumOptionsVehicleCategoryReset`.
  usePremiumOptionsVehicleCategoryReset(premiumOptionsVehicleCategory, setPremiumOptions);

  // Shared by every branch — the step exists in MOTOR/LIFE/HEALTH_STEPS and in
  // the generated generic step list, always directly after Insurance Type.
  const renderPremiumOptionsStep = () => (
    <PremiumOptionsStep
      insuranceTypeId={insuranceTypeId}
      insuranceTypeLabel={insuranceTypeLabel}
      vehicleCategory={premiumOptionsVehicleCategory}
      productVariant={premiumOptionsProductVariant}
      options={premiumOptions}
      onChange={setPremiumOptions}
      showErrors={premiumOptionsTouched}
    />
  );

  const renderMotorOrLandingStepContent = () => {
    if (stepId === 'insurance-type') {
      return (
        <div className="space-y-6">
          <InsuranceTypeStep
            value={insuranceType}
            onChange={handleInsuranceTypeChange}
            options={insuranceTypeOptions}
            loading={typesLoading}
          />
          {!isMotor && (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {typesLoading
                ? 'Loading insurance types…'
                : 'This insurance type is not configured yet. Pick one from the list above to continue.'}
            </div>
          )}
        </div>
      );
    }

    // A non-motor type picked on the landing step still renders its own
    // Premium Options step from here.
    if (!isMotor) return stepId === PREMIUM_OPTIONS_STEP_ID ? renderPremiumOptionsStep() : null;

    switch (stepId) {
      case 'customer':
        // B-02/C1: motor only -- the PIN is what the insurer zone-rates on.
        return <CustomerStep requirePincode />;
      case 'business-type':
        return <BusinessTypeStep />;
      case VEHICLE_DETAILS_STEP_ID:
        return <VehicleDetailsStep />;
      case PRODUCT_PREMIUM_STEP_ID:
        // Cover choice first, then the insurer quotes being compared for it.
        return (
          <div className="space-y-8">
            <ProductVariantStep />
            <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
              {renderPremiumOptionsStep()}
            </div>
          </div>
        );
      case 'review':
        return (
          <MotorReviewStep
            proposerName={proposerName}
            proposerPhone={proposerPhone}
            proposerEmail={proposerEmail}
          />
        );
      default:
        return null;
    }
  };

  const renderLifeStepContent = () => {
    if (stepId === 'insurance-type') {
      return (
        <InsuranceTypeStep
          value={insuranceType}
          onChange={handleInsuranceTypeChange}
          options={insuranceTypeOptions}
          loading={typesLoading}
        />
      );
    }
    if (stepId === PREMIUM_OPTIONS_STEP_ID) return renderPremiumOptionsStep();

    switch (stepId) {
      case 'basic-details':
        return <LifeBasicDetailsStep />;
      case 'product-details':
        return <LifeProductDetailsStep />;
      case 'review':
        return <LifeReviewStep proposerPhone={proposerPhone} proposerEmail={proposerEmail} />;
      default:
        return null;
    }
  };

  const renderHealthStepContent = () => {
    if (stepId === 'insurance-type') {
      return (
        <InsuranceTypeStep
          value={insuranceType}
          onChange={handleInsuranceTypeChange}
          options={insuranceTypeOptions}
          loading={typesLoading}
        />
      );
    }
    if (stepId === PREMIUM_OPTIONS_STEP_ID) return renderPremiumOptionsStep();

    switch (stepId) {
      case 'product-details':
        return <HealthProductDetailsStep />;
      case 'insured-details':
        return <HealthInsuredDetailsStep />;
      case 'premium-details':
        return <HealthPremiumDetailsStep />;
      case 'additional-details':
        return <HealthAdditionalDetailsStep />;
      case 'review':
        return (
          <HealthReviewStep
            proposerName={proposerName}
            proposerPhone={proposerPhone}
            proposerEmail={proposerEmail}
          />
        );
      default:
        return null;
    }
  };

  const genericGroupLabel = (genericGroups ?? []).find((g) => g.id === genericGroup)?.name;
  const genericSubgroupLabel = (genericSubgroups ?? []).find((s) => s.id === genericSubgroup)?.name;

  const renderGenericStepContent = () => {
    if (stepId === GENERIC_STEP_INSURANCE_TYPE) {
      return (
        <InsuranceTypeStep
          value={insuranceType}
          onChange={handleInsuranceTypeChange}
          options={insuranceTypeOptions}
          loading={typesLoading}
        />
      );
    }
    if (stepId === PREMIUM_OPTIONS_STEP_ID) return renderPremiumOptionsStep();
    if (stepId === GENERIC_STEP_CUSTOMER) {
      // Reused verbatim from the motor branch: it reads and writes the same
      // `customer_*` keys the generic schema declares, so there is nothing
      // motor-specific left in it.
      return <CustomerStep />;
    }
    if (stepId === GENERIC_STEP_DETAILS) {
      return <GenericDetailsStep insuranceTypeId={insuranceTypeId} />;
    }
    if (stepId === GENERIC_STEP_REVIEW) {
      return (
        <GenericReviewStep
          insuranceTypeLabel={insuranceTypeLabel}
          fields={schemaFields}
          premiumOptions={premiumOptions}
          groupLabel={genericGroupLabel}
          subgroupLabel={genericSubgroupLabel}
        />
      );
    }
    const section = sectionFromStepId(stepId ?? '');
    if (section !== null) {
      return <DynamicQuoteSection section={section} fields={genericFields} />;
    }
    return null;
  };

  const isLastStep = currentStep === steps.length - 1;

  // Life's terminal "awaiting insurer integration" state replaces the whole
  // wizard body (stepper included), mirroring how the admin onboarding
  // wizards swap to <WizardSuccess/> once their submit resolves.
  if (isLife && lifeSubmitResult) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card padding="lg">
          <AwaitingIntegrationPanel
            requestNumber={lifeSubmitResult.request_id || lifeSubmitResult.quote_request_id}
            message={lifeSubmitResult.message}
            onDone={() => navigate('/quotes')}
          />
        </Card>
      </div>
    );
  }

  // Same terminal state for Health — also no insurer adapter yet.
  if (isHealth && healthSubmitResult) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card padding="lg">
          <HealthAwaitingIntegrationPanel
            requestNumber={healthSubmitResult.request_id || healthSubmitResult.quote_request_id}
            message={healthSubmitResult.message}
            onDone={() => navigate('/quotes')}
          />
        </Card>
      </div>
    );
  }

  if (isGeneric) {
    return (
      <FormProvider {...genericMethods}>
        <WizardContainer
          title="New Quote"
          description="Get quotes from multiple insurers in one place."
          steps={steps}
          currentStep={currentStep}
          onStepClick={(i) => {
            if (i <= currentStep) setCurrentStep(i);
          }}
        >
          <ErrorSummary messages={serverErrors} />
          {renewalLinkUi}
          {/* Only a section step genuinely cannot render without the schema —
              the four fixed steps stand on their own, so they keep rendering
              while a group/sub-group change re-resolves it. */}
          {schemaLoading && schemaFields.length === 0 && sectionFromStepId(stepId ?? '') !== null ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading this insurance type&rsquo;s form&hellip;
            </p>
          ) : (
            renderGenericStepContent()
          )}

          <WizardNavigation
            isFirst={currentStep === 0}
            isLast={isLastStep}
            isSubmitting={genericSubmitting || flushingOptions}
            canGoNext={canGoNext}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onComplete={handleComplete}
            completeLabel="Submit"
          />
        </WizardContainer>
      </FormProvider>
    );
  }

  if (isLife) {
    return (
      <FormProvider {...lifeMethods}>
        <WizardContainer
          title="New Quote"
          description="Get quotes from multiple insurers in one place."
          steps={steps}
          currentStep={currentStep}
          onStepClick={(i) => {
            if (i <= currentStep) setCurrentStep(i);
          }}
        >
          <ErrorSummary messages={serverErrors} />
          {renewalLinkUi}
          {renderLifeStepContent()}

          <WizardNavigation
            isFirst={currentStep === 0}
            isLast={isLastStep}
            isSubmitting={submitMutation.isPending || flushingOptions}
            canGoNext={canGoNext}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onComplete={handleComplete}
            completeLabel="Submit"
          />
        </WizardContainer>
      </FormProvider>
    );
  }

  if (isHealth) {
    return (
      <FormProvider {...healthMethods}>
        <WizardContainer
          title="New Quote"
          description="Get quotes from multiple insurers in one place."
          steps={steps}
          currentStep={currentStep}
          onStepClick={(i) => {
            if (i <= currentStep) setCurrentStep(i);
          }}
        >
          <ErrorSummary messages={serverErrors} />
          {renewalLinkUi}
          {renderHealthStepContent()}

          <WizardNavigation
            isFirst={currentStep === 0}
            isLast={isLastStep}
            isSubmitting={submitMutation.isPending || flushingOptions}
            canGoNext={canGoNext}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onComplete={handleComplete}
            completeLabel="Calculate Premium"
          />
        </WizardContainer>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...motorMethods}>
      <WizardContainer
        title="New Quote"
        description="Get quotes from multiple insurers in one place."
        steps={steps}
        currentStep={currentStep}
        onStepClick={(i) => {
          if (i <= currentStep) setCurrentStep(i);
        }}
      >
        <ErrorSummary messages={serverErrors} />
          {renewalLinkUi}
        {renderMotorOrLandingStepContent()}

        <WizardNavigation
          isFirst={currentStep === 0}
          isLast={isLastStep}
          isSubmitting={submitMutation.isPending || flushingOptions}
          canGoNext={canGoNext}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onComplete={handleComplete}
          completeLabel="Submit"
        />
      </WizardContainer>
    </FormProvider>
  );
}

export default QuoteWizardPage;
